import { AccessControlled } from '@/components/access-control'
import { TabCountBadge } from '@/components/common/TabCountBadge'
import {
	usePageHeaderActions,
	usePageHeaderBackButton,
	usePageHeaderTabs,
} from '@/components/page-context/HeaderActionsHook'
import { NO_TAB_COUNTS, useClearFilters, usePageContext } from '@/components/page-context/context'
import type {
	BackButtonConfig,
	ButtonPageAction,
	OverflowTab,
	PageAction,
	PageTab,
} from '@/components/page-context/types'
import { SlidingIndicator, useSlidingIndicator } from '@/components/sliding-indicator/SlidingIndicator'
import { Spinner } from '@/components/ui/spinner'
import { useAccessControl } from '@/hooks/access-control/useAccessControl'
import { useIsMobile } from '@/hooks/use-mobile'
import { usePageTabCounts } from '@/hooks/usePageTabCounts'
import { useQuotaUsageTooltip } from '@/hooks/useQuotaUsageTooltip'
import { getNavRouteTarget } from '@/lib/navRoutes'
import { isOrganizationLevelRoute, stripScopePrefix } from '@/lib/routeScope'
import { useRouteLocation } from '@/lib/routerUtils'
import { cn } from '@/lib/utils'
import { useDeviceFilterStore } from '@/stores/deviceFilterStore'
import { Link, type LinkProps, useMatches, useNavigate } from '@tanstack/react-router'
import { ChevronDown, ChevronLeft } from 'lucide-react'
import * as React from 'react'
import {
	type NavItem,
	getResourceForPath,
	isUnderBasePath,
	navData,
	organizationNavData,
	organizationSettingsNavData,
	settingsNavData,
	stripDefaultTabSegment,
} from '../data/nav-data'
import { OverflowTabsDropdown } from './overflow-tabs/OverflowTabsDropdown'

import { Button, buttonVariants } from './ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu'
import { Separator } from './ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

/** The dropdown wears the active tab's name when that tab is hidden inside it, otherwise a count. */
function getDropdownLabelAndCount(
	placement: TabPlacement,
	hiddenCount: number,
): { label: string; count: number | null } {
	const { activeTab, isActiveInDropdown } = placement
	if (activeTab && isActiveInDropdown) {
		return { label: activeTab.name, count: activeTab.showCount === false ? null : (activeTab.count ?? null) }
	}
	return { label: `${hiddenCount} more`, count: null }
}

// Shared by both strip tabs. Only the active tone is shared: each carries `group` itself, and the two
// reach their inactive hover color differently, so those strings are not interchangeable.
const TAB_TONE_ACTIVE = 'text-header-tab-active-text bg-header-tab-active-bg hover:bg-header-tab-active-bg-hover'

/** Divides a tab strip from the dropdown that holds what did not fit. */
function TabStripSeparator({ dataTestId }: { dataTestId: string }) {
	return <Separator orientation='vertical' className='h-(--icon-standalone)! mx-2' data-testid={dataTestId} />
}

interface TabButtonProps {
	tab: PageTab
}

export function TabButton({ tab }: TabButtonProps) {
	return (
		<button
			type='button'
			key={tab.key}
			onClick={tab.onClick}
			data-testid={`page-header-tab-${tab.key}`}
			data-active={tab.isActive ? 'true' : 'false'}
			aria-current={tab.isActive ? 'page' : undefined}
			className={cn(
				'group inline-flex h-control items-center gap-1.5 px-3 font-medium rounded-[6px] whitespace-nowrap transition-colors duration-300 ease-in-out cursor-pointer text-body!',
				tab.isActive
					? TAB_TONE_ACTIVE
					: 'bg-header-tab-inactive-bg text-header-tab-inactive-text group-hover:text-header-tab-active-text',
			)}>
			<span
				className={cn(
					tab.isActive
						? 'text-header-tab-active-text'
						: 'text-header-tab-inactive-text group-hover:text-header-tab-active-text',
				)}>
				{tab.name}
			</span>
			<TabCountBadge
				count={tab.count}
				active={tab.isActive}
				showCount={tab.showCount}
				loading={tab.countLoading}
				animateOnce={tab.animateCountOnce}
			/>
		</button>
	)
}

export function getTabsForPath(navData: NavItem[], path: string): NavItem[] {
	// Dashboards tabs (All, Device, Fleet) should stay on the dashboards page only and not move to child pages
	if (
		path.startsWith('/data/dashboards') &&
		!path.startsWith('/data/dashboards/all') &&
		!path.startsWith('/data/dashboards/device') &&
		!path.startsWith('/data/dashboards/fleet')
	) {
		return []
	}

	if (path.startsWith('/settings')) {
		const allSettingsNavData = [...settingsNavData, ...organizationSettingsNavData]
		for (const item of allSettingsNavData) {
			if (item.path && isUnderBasePath(path, stripDefaultTabSegment(item.path))) {
				return item.items || []
			}
		}
	}

	for (const section of navData) {
		if (section.settingsOptions) {
			for (const settingsOption of section.settingsOptions) {
				if (settingsOption.path && isUnderBasePath(path, stripDefaultTabSegment(settingsOption.path))) {
					return settingsOption.items || []
				}
			}
		}
	}

	const findMatchingTabs = (items: NavItem[]): NavItem[] => {
		for (const item of items) {
			if (item.path) {
				const basePath = item.path.substring(0, item.path.lastIndexOf('/'))
				if (basePath && isUnderBasePath(path, basePath)) {
					if (item.items && item.items.length > 0) {
						return item.items
					}
				}
			}
			if (item.items && item.items.length > 0) {
				const found = findMatchingTabs(item.items)
				if (found.length > 0) {
					return found
				}
			}
		}
		return []
	}

	return findMatchingTabs(navData)
}

function useTabs(navData: NavItem[]) {
	const { pathname } = useRouteLocation()

	return React.useMemo(() => {
		const normalized = stripScopePrefix(pathname)
		return getTabsForPath(navData, normalized)
	}, [navData, pathname])
}

function RouterTab({
	to,
	params,
	search,
	active,
	count,
	showCount,
	children,
	onClick,
	tabRef,
	dataTestId,
}: LinkProps & {
	active?: boolean
	count?: number
	showCount?: boolean
	children: React.ReactNode
	onClick?: () => void
	tabRef?: React.Ref<HTMLElement>
	dataTestId?: string
}) {
	return (
		// The span is only the box the sliding indicator measures. The handler belongs on the link itself:
		// on the wrapper it also fired for Space, which no anchor navigates on, moving the indicator to a
		// tab the route never changed to.
		<span ref={tabRef} className='inline-flex'>
			<Link
				to={to}
				params={params}
				search={search}
				onClick={onClick}
				aria-current={active ? 'page' : undefined}
				data-testid={dataTestId}
				className={cn(
					'group inline-flex h-control items-center gap-1.5 px-3 text-body font-medium rounded-[6px] whitespace-nowrap cursor-pointer',
					'relative z-10 transition-colors duration-300 ease-in-out',
					active
						? TAB_TONE_ACTIVE
						: 'bg-header-tab-inactive-bg text-header-tab-inactive-text hover:text-header-tab-active-text',
				)}>
				<span className='text-body'>{children}</span>
				<TabCountBadge count={count} active={active} showCount={showCount} />
			</Link>
		</span>
	)
}

/**
 * With `view` denied, the header keeps only what still works without read access: icon-only navigation
 * buttons (Remote Commands -> Assets, Sessions -> Session Rules) and create CTAs the role actually holds.
 *
 * `isCreateDenied` mirrors what `AccessControlled` does with the same grant, so a CTA it would hide never
 * counts as content and leave an empty header behind.
 */
function isActionVisibleWithoutViewAccess(action: PageAction, isCreateDenied: boolean): boolean {
	if (action.type === 'component') {
		return false
	}
	if (!action.label || action.label.trim() === '') {
		return true
	}
	return action.access?.action === 'create' && !isCreateDenied
}

const MAX_VISIBLE_TABS = 3 // All tab + 2 other tabs

/** The run of tabs that fits in the strip. Whatever is left over lives in the dropdown. */
function visibleRun<T>(tabs: T[]): T[] {
	return tabs.slice(0, MAX_VISIBLE_TABS)
}

/** The shape both strips share: router tabs, page registered tabs and overflow tabs all satisfy it. */
interface TabLike {
	key: string
	name: string
	count?: number
	showCount?: boolean
	isActive?: boolean
}

interface TabPlacement {
	/** Slot the sliding indicator points at. The dropdown button occupies the slot after the visible run. */
	activeIndex: number
	isActiveInDropdown: boolean
	/** The active tab wherever it sits, so the dropdown can wear its name instead of a count. */
	activeTab: TabLike | undefined
}

/**
 * Where the active tab sits once the strip is cut down to what fits. A tab hidden in the dropdown pins
 * the indicator to the dropdown button rather than to a slot the user cannot see.
 *
 * `visible` is what renders, `all` is every tab the dropdown could list. A tab in `all` that `visible`
 * does not carry is hidden. With nothing active anywhere the indicator falls back to the first slot.
 */
export function locateActiveTab(visible: TabLike[], all: TabLike[]): TabPlacement {
	const hiddenActiveTab = all.find(tab => tab.isActive && !visible.some(shown => shown.key === tab.key))
	if (hiddenActiveTab) {
		return { activeIndex: visible.length, isActiveInDropdown: true, activeTab: hiddenActiveTab }
	}

	const visibleIndex = visible.findIndex(tab => tab.isActive)
	return {
		activeIndex: visibleIndex === -1 ? 0 : visibleIndex,
		isActiveInDropdown: false,
		activeTab: visible[visibleIndex],
	}
}

function HeaderBackAndTitleControls({
	backButton,
	titleActions,
	backButtonClassName,
}: {
	backButton: BackButtonConfig | null | undefined
	titleActions: PageAction[]
	backButtonClassName?: string
}) {
	const navigate = useNavigate()
	return (
		<>
			{backButton && (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant='ghost'
							size='icon'
							className={cn('size-control mr-2 shrink-0', backButtonClassName)}
							onClick={() => {
								navigate({
									to: backButton.to,
									params: backButton.params,
								})
							}}>
							<ChevronLeft className='size-(--icon-standalone)' />
						</Button>
					</TooltipTrigger>
					<TooltipContent>{backButton.tooltip || 'Go back'}</TooltipContent>
				</Tooltip>
			)}
			{titleActions.length > 0 && (
				<div className='flex items-center gap-2 mr-2 min-w-0'>
					{titleActions.map(action => {
						if (action.type !== 'component') {
							return null
						}
						return (
							<div key={action.id} className='min-w-0'>
								{action.component}
							</div>
						)
					})}
				</div>
			)}
		</>
	)
}

interface PageHeaderButtonActionProps {
	action: ButtonPageAction
	isMobile: boolean
}

function PageHeaderButtonAction({ action, isMobile }: PageHeaderButtonActionProps) {
	const access = useAccessControl(action.access ?? {})
	const tooltip = useQuotaUsageTooltip(action.tooltip, action.access)

	const isIconOnly = !action.label || action.label.trim() === ''
	const isMobileIconOnly = isMobile && Boolean(action.icon)
	const effectiveIsIconOnly = isIconOnly || isMobileIconOnly
	const hasChildren = action.children && action.children.length > 0

	// Once the label is off screen the icon is all that is left, and an icon names nothing. Falls back to
	// the author's own tooltip rather than the computed one, which may carry quota usage instead of a name.
	const accessibleName = action.label.trim() || action.tooltip

	const primaryButton = (
		<Button
			key={action.id}
			variant={action.variant || 'default'}
			size={effectiveIsIconOnly ? 'icon' : 'sm'}
			onClick={action.onClick}
			disabled={action.disabled || action.loading}
			aria-label={effectiveIsIconOnly ? accessibleName : undefined}
			data-testid={action.dataTestId}
			className={cn(
				'transition-all duration-200',
				effectiveIsIconOnly ? 'size-(--height-control)' : 'h-(--height-control)',
				hasChildren && 'control-base-split-r',
			)}>
			{action.loading && <Spinner className='mr-1' />}
			{action.icon && !action.loading && <span className='shrink-0'>{action.icon}</span>}
			{!effectiveIsIconOnly && <span className='whitespace-nowrap text-control'>{action.label}</span>}
		</Button>
	)

	let childActions: React.ReactNode | null = null
	if (hasChildren) {
		childActions = (
			<DropdownMenu>
				<DropdownMenuTrigger
					asChild
					className='rounded-l-none border-l-0 bg-primary control-base control-base-split-l text-primary-foreground hover:bg-primary/90 px-0'>
					<Button
						aria-label={accessibleName ? `${accessibleName} options` : 'More options'}
						className={cn(
							buttonVariants({
								variant: action.variant || 'default',
								size: 'sm',
							}),
							'rounded-l-none px-2 border-l-0 h-control',
						)}>
						<ChevronDown className='size-(--icon-standalone)' />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent side='bottom' align='end' sideOffset={0} className='min-w-40 rounded-t-none mt-1'>
					{action.children?.map(child => (
						<DropdownMenuItem
							key={child.id}
							disabled={child.disabled || action.loading}
							className='text-body'
							onSelect={event => {
								event.preventDefault()
								if (!child.disabled) {
									child.onClick()
								}
							}}>
							{action.loading && <Spinner className='mr-1' />}
							{child.icon && !action.loading && <span className='shrink-0'>{child.icon}</span>}
							<span>{child.label}</span>
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		)
	}

	const renderedPrimaryButton = tooltip ? (
		<Tooltip key={action.id}>
			<TooltipTrigger asChild>{primaryButton}</TooltipTrigger>
			<TooltipContent side='bottom' sideOffset={8} data-testid={`${action.id}-tooltip`}>
				<p>{tooltip}</p>
			</TooltipContent>
		</Tooltip>
	) : (
		primaryButton
	)

	// 1. If warning state: warning tooltip is ONLY on the primary button. Chevron/dropdown options remain active and no tooltip.
	if (action.access && access.state === 'warning') {
		const wrappedPrimaryButton = (
			<AccessControlled key={action.id} {...action.access}>
				{renderedPrimaryButton}
			</AccessControlled>
		)

		return (
			<div key={action.id} className={cn('inline-flex items-stretch overflow-hidden')}>
				{wrappedPrimaryButton}
				{hasChildren && <Separator orientation='vertical' />}
				{childActions}
			</div>
		)
	}

	// 2. If blocked/denied or any other state: wrap the entire button group in AccessControlled (so entire group is disabled/restricted).
	const buttonWithChildren = (
		<div key={action.id} className={cn('inline-flex items-stretch overflow-hidden')}>
			{renderedPrimaryButton}
			{hasChildren && <Separator orientation='vertical' />}
			{childActions}
		</div>
	)

	if (action.access) {
		return (
			<AccessControlled key={action.id} {...action.access}>
				{buttonWithChildren}
			</AccessControlled>
		)
	}

	return buttonWithChildren
}

export function PageHeader() {
	const { pathname: currentPath, search: currentSearch, tenantId, orgId } = useRouteLocation()
	const isMobile = useIsMobile()
	const navigate = useNavigate()
	const { clearFilters } = useDeviceFilterStore()
	const tabSearch = React.useMemo(() => {
		if (!currentSearch || typeof currentSearch !== 'object') {
			return undefined
		}
		const { filters, ...rest } = currentSearch as Record<string, unknown>
		return Object.keys(rest).length > 0 ? rest : undefined
	}, [currentSearch])
	const normalizedPath = stripScopePrefix(currentPath)
	const { actions: rawActions } = usePageHeaderActions()
	const { tabs: dynamicTabs } = usePageHeaderTabs()
	const {
		setHeaderControlsSlot,
		setIsPageHeaderEmpty,
		overflowTabs: config,
		hideOverflowTabs,
		clearPageActions,
		clearPageTabs,
		clearBackButton,
		setOverflowTabs,
		setHideOverflowTabs,
		setTabCounts,
	} = usePageContext()
	const { clearRegularFilters } = useClearFilters()
	const { backButton } = usePageHeaderBackButton()

	// Everything a page registers into the header lives in one shared context, so whatever the last page
	// put there is still on screen until the next page happens to overwrite it. Pages used to clear on
	// unmount, but most never did, and a page that registers its actions only once its data arrives left
	// the previous page's CTA sitting in the header until then.
	//
	// Keyed on the matched route rather than the pathname: a `$type` tab change keeps the same route and
	// the same page, and clearing there would blink the header on every tab click.
	//
	// PageHeader is an earlier sibling of the outlet, so its passive effect runs before the incoming page's
	// own effects and cannot wipe what that page has just registered.
	const routeId = useMatches({ select: matches => matches[matches.length - 1]?.routeId })

	// biome-ignore lint/correctness/useExhaustiveDependencies: the ids are triggers, not values read here
	React.useEffect(() => {
		clearPageActions()
		clearPageTabs()
		clearBackButton()
		setOverflowTabs(null)
		setHideOverflowTabs(false)
		setTabCounts(NO_TAB_COUNTS)
	}, [
		routeId,
		tenantId,
		orgId,
		clearPageActions,
		clearPageTabs,
		clearBackButton,
		setOverflowTabs,
		setHideOverflowTabs,
		setTabCounts,
	])

	// Switching tabs drops the filters the previous tab was showing: the page's own search bar chips
	// via the header context, and the shared device filter store.
	const clearFiltersForTabChange = React.useCallback(() => {
		clearRegularFilters()
		clearFilters()
	}, [clearRegularFilters, clearFilters])
	const isOrgLevel = isOrganizationLevelRoute(currentPath)
	// Tab targets resolve exactly like sidebar links do, off the nav path rather than the current URL.
	const navTargetFor = React.useCallback(
		(itemPath: string) => getNavRouteTarget(itemPath, { orgId, tenantId, routeTenantId: tenantId }),
		[orgId, tenantId],
	)
	const tabsToDisplay = useTabs(isOrgLevel ? organizationNavData : navData)
	const { tabCounts } = usePageTabCounts()
	const activeResource = React.useMemo(() => getResourceForPath(normalizedPath), [normalizedPath])
	const accessState = useAccessControl({
		resource: activeResource as Parameters<typeof useAccessControl>[0]['resource'],
		action: 'view',
	})
	const isAccessRestricted = activeResource !== undefined && accessState.state === 'denied'
	const createAccessState = useAccessControl({
		resource: activeResource as Parameters<typeof useAccessControl>[0]['resource'],
		action: 'create',
	})
	const isCreateDenied = createAccessState.state === 'denied'

	// Split actions into left-aligned "title" components and regular right-side actions
	const allTitleActions =
		rawActions?.filter(action => action.type === 'component' && action.id.endsWith('-title')) ?? []
	const allActions = rawActions?.filter(action => !(action.type === 'component' && action.id.endsWith('-title'))) ?? []

	// Read denied: strip the header rather than pair an "Access restricted" body with tabs counting zero.
	const titleActions = isAccessRestricted ? [] : allTitleActions
	const visibleActions = isAccessRestricted
		? allActions.filter(action => isActionVisibleWithoutViewAccess(action, isCreateDenied))
		: allActions
	const hasVisibleTabs = !isAccessRestricted && (tabsToDisplay.length > 0 || Boolean(dynamicTabs))
	const isHeaderEmpty = !hasVisibleTabs && !visibleActions.length && !titleActions.length && !backButton
	const [activeIndex, setActiveIndex] = React.useState<number>(0)
	const tabRefs = React.useRef<(HTMLElement | null)[]>([])
	const dynamicTabsContainerRef = React.useRef<HTMLDivElement>(null)
	const dynamicTabsRefs = React.useRef<(HTMLElement | null)[]>([])
	const containerRef = React.useRef<HTMLDivElement>(null)

	const usingOverflowTabs = config?.tabs !== undefined
	const visibleRouterTabsCount = usingOverflowTabs ? 0 : Math.min(tabsToDisplay.length, MAX_VISIBLE_TABS)
	const overflowActionTabs = React.useMemo(() => config?.tabs ?? [], [config?.tabs])

	// The full run of slots: these tabs exist only when the page registered its own, which is the case
	// that leaves no router tabs beside them.
	const visibleOverflowTabs = React.useMemo(() => visibleRun(overflowActionTabs), [overflowActionTabs])

	const effectiveBaseIndex = usingOverflowTabs ? visibleOverflowTabs.length : visibleRouterTabsCount

	const logicalActiveRouterIndex = tabsToDisplay.findIndex(tab => {
		if (!tab.path) {
			return false
		}
		const tabPath = tab.path.replace(/^\//, '')
		const currentNormalizedPath = normalizedPath.replace(/^\//, '')
		return currentNormalizedPath === tabPath || currentNormalizedPath.startsWith(`${tabPath}/`)
	})

	// Active state detection (independent of visual activeIndex)
	const isActiveActionTabInDropdown = locateActiveTab(visibleOverflowTabs, overflowActionTabs).isActiveInDropdown
	const isActiveRouterTabInOverflow = logicalActiveRouterIndex >= MAX_VISIBLE_TABS && !usingOverflowTabs

	const isSomethingInDropdownActive = isActiveActionTabInDropdown || isActiveRouterTabInOverflow

	// Which of the two tab strips renders, decided once so the render helpers below can read it.
	//
	// The overflow strip is gated on having something to put in it, not on a page having registered.
	// Registration used to double as the switch for the router tab dropdown, so a page with more nav
	// tabs than fit had to register to keep them reachable, and lost them silently if it forgot.
	const hasOverflowTabContent = usingOverflowTabs || tabsToDisplay.length > MAX_VISIBLE_TABS
	const shouldShowRouterTabs = !isAccessRestricted && tabsToDisplay.length > 0 && !usingOverflowTabs
	const shouldShowOverflowTabs =
		!isAccessRestricted &&
		hasOverflowTabContent &&
		!hideOverflowTabs &&
		!(usingOverflowTabs && overflowActionTabs.length === 0)
	const isAnyTabVisible = shouldShowRouterTabs || shouldShowOverflowTabs
	const shouldShowDynamicTabs = Boolean(dynamicTabs) && !isAccessRestricted

	const indicatorStyle = useSlidingIndicator(
		activeIndex,
		tabRefs,
		containerRef,
		{ hideWhenUnmeasurable: tabsToDisplay.length === 0 && overflowActionTabs.length === 0 },
		// tabCounts and backButton are triggers rather than values read here: both change the width of
		// the tab strip, so the indicator has to remeasure when they land.
		[activeIndex, tabsToDisplay, overflowActionTabs, backButton, tabCounts],
	)

	// Derived rather than state: the dynamic strip's active tab follows entirely from the registered
	// tabs, and keeping it in state made the first measurement run against the previous index.
	const dynamicTabsActiveIndex = React.useMemo(
		() => locateActiveTab(visibleRun(dynamicTabs?.visibleTabs ?? []), dynamicTabs?.allTabs ?? []).activeIndex,
		[dynamicTabs?.visibleTabs, dynamicTabs?.allTabs],
	)

	const dynamicTabsIndicatorStyle = useSlidingIndicator(
		dynamicTabsActiveIndex,
		dynamicTabsRefs,
		dynamicTabsContainerRef,
		{ hideWhenUnmeasurable: (dynamicTabs?.visibleTabs?.length ?? 0) === 0 },
		[dynamicTabsActiveIndex, dynamicTabs?.visibleTabs, dynamicTabs?.allTabs],
	)

	// Set activeIndex from the URL, synchronously before paint so the indicator does not flicker.
	React.useLayoutEffect(() => {
		// Gated on having tabs to point at, not on a registered page key: pages that show plain router
		// tabs never register overflow tabs, and keying on the page key left their indicator on tab 0.
		if (tabsToDisplay.length === 0 && overflowActionTabs.length === 0) {
			return
		}

		// 1. An active tab that lives in the dropdown pins focus to the dropdown button.
		const placement = locateActiveTab(visibleOverflowTabs, overflowActionTabs)
		const isActiveRouterTabInDropdown = logicalActiveRouterIndex >= MAX_VISIBLE_TABS && !usingOverflowTabs

		if (placement.isActiveInDropdown || isActiveRouterTabInDropdown) {
			setActiveIndex(effectiveBaseIndex)
			return
		}

		// 2. A visible overflow action tab takes its own slot.
		if (placement.activeTab) {
			setActiveIndex(placement.activeIndex)
			return
		}

		// 3. Otherwise follow the URL.
		setActiveIndex(logicalActiveRouterIndex >= 0 ? logicalActiveRouterIndex : 0)
	}, [
		tabsToDisplay,
		overflowActionTabs,
		effectiveBaseIndex,
		logicalActiveRouterIndex,
		visibleOverflowTabs,
		usingOverflowTabs,
	])

	// Lets the layout drop the header strip rather than paint an empty bar. Restricted routes only: a header
	// is also momentarily empty between one page clearing its actions and the next registering them, and
	// collapsing there would bounce the page content up and back down on every navigation.
	const isHeaderRetired = isHeaderEmpty && isAccessRestricted
	// Layout, not passive: RootLayout collapses the strip off this flag, so reporting after paint shows a
	// full height header bar for one frame before it drops.
	React.useLayoutEffect(() => {
		setIsPageHeaderEmpty(isHeaderRetired)
		return () => setIsPageHeaderEmpty(false)
	}, [isHeaderRetired, setIsPageHeaderEmpty])

	if (isHeaderEmpty) {
		return null
	}

	const renderActions = () => {
		if (!visibleActions.length) {
			return null
		}

		return (
			<div className='flex items-center gap-1 sm:gap-2 shrink-0' data-clears-selection='true'>
				{visibleActions.map(action => {
					if (action.type === 'component') {
						const componentNode = <div key={action.id}>{action.component}</div>
						return action.access ? (
							<AccessControlled key={action.id} {...action.access}>
								{componentNode}
							</AccessControlled>
						) : (
							componentNode
						)
					}

					return <PageHeaderButtonAction key={action.id} action={action} isMobile={isMobile} />
				})}
			</div>
		)
	}

	const renderDynamicTabs = () => {
		if (!dynamicTabs) {
			return null
		}

		const { visibleTabs, allTabs, hideTabsSeparator } = dynamicTabs

		const actualOverflowCount = Math.max(0, visibleTabs.length - MAX_VISIBLE_TABS)
		const hasOverflowTabs = Boolean(allTabs && allTabs.length > 0 && actualOverflowCount > 0)

		const handleDynamicTabsOverflowTabClick = (tab: OverflowTab) => {
			clearFiltersForTabChange()
			tab.onClick?.()
		}

		// Only list tabs in the dropdown when some actually overflow, so a visible navigation tab
		// never appears twice.
		const dropdownAllTabs: OverflowTab[] =
			actualOverflowCount > 0
				? (allTabs || [])
						.filter(tab => tab.key !== 'all')
						.map(tab => ({ ...tab, count: tab.count ?? 0, isActive: tab.isActive ?? false }))
				: []

		const displayedTabs = visibleRun(visibleTabs)
		const placement = locateActiveTab(displayedTabs, dropdownAllTabs)
		const dynamicDropdown = getDropdownLabelAndCount(placement, actualOverflowCount)

		return (
			<div ref={dynamicTabsContainerRef} className='flex items-center gap-1 relative z-10'>
				{displayedTabs.length > 0 && (
					<SlidingIndicator style={dynamicTabsIndicatorStyle} dataTestId='page-header-dynamic-tab-indicator' />
				)}

				<div className='flex items-center gap-1 relative z-10'>
					{displayedTabs.map((tab, index) => (
						<div
							key={tab.key}
							ref={el => {
								dynamicTabsRefs.current[index] = el
							}}>
							<TabButton
								tab={{
									...tab,
									onClick: () => {
										clearFiltersForTabChange()
										tab.onClick?.()
									},
								}}
							/>
						</div>
					))}

					{!hideTabsSeparator && displayedTabs.length > 0 && hasOverflowTabs && (
						<TabStripSeparator dataTestId='page-header-tabs-separator' />
					)}

					{hasOverflowTabs && (
						<OverflowTabsDropdown
							tabs={dropdownAllTabs}
							dropdownLabel={dynamicDropdown.label}
							dropdownCount={dynamicDropdown.count}
							dropdownRef={el => {
								dynamicTabsRefs.current[displayedTabs.length] = el
							}}
							totalTabsCount={allTabs?.length || 0}
							onTabClick={handleDynamicTabsOverflowTabClick}
						/>
					)}
				</div>
			</div>
		)
	}

	const handleTabClickWithClear = (index: number, onClick?: () => void) => {
		setActiveIndex(index)
		onClick?.()
		clearFiltersForTabChange()
	}

	const renderOverflowTabsSection = () => {
		if (!shouldShowOverflowTabs) {
			return null
		}

		const overflowRouterTabs = usingOverflowTabs ? [] : tabsToDisplay.slice(MAX_VISIBLE_TABS)

		const routerTabsForDropdown: OverflowTab[] = overflowRouterTabs.map(item => {
			const pathParts = item.path.split('/')
			const lastPart = pathParts[pathParts.length - 1]
			const tabCount = isAccessRestricted ? 0 : tabCounts[lastPart] || 0
			const { to, params } = navTargetFor(item.path)

			// Match against the original index so activeIndex handling stays aligned with tabsToDisplay
			const originalIndex = tabsToDisplay.findIndex(tab => tab.id === item.id)

			return {
				name: item.title,
				count: tabCount,
				key: item.id,
				isActive: logicalActiveRouterIndex === originalIndex,
				onClick: () => {
					clearFiltersForTabChange()
					navigate({ to, params, search: tabSearch })
				},
			}
		})

		// Action tabs only join the dropdown when some of them do not fit as visible tabs. The dropdown
		// then lists ALL of them so a drag can produce a complete order, but the label counts only the
		// hidden ones.
		const hasActionTabOverflow = overflowActionTabs.length > visibleOverflowTabs.length
		const dropdownOverflowTabs = [
			...routerTabsForDropdown,
			...(hasActionTabOverflow ? overflowActionTabs.filter(tab => tab.key !== 'all') : []),
		]
		const hiddenOverflowTabsCount =
			routerTabsForDropdown.length + Math.max(0, overflowActionTabs.length - visibleOverflowTabs.length)

		const totalAllTabs = (usingOverflowTabs ? 0 : tabsToDisplay.length) + overflowActionTabs.length
		const shouldShowDropdown = dropdownOverflowTabs.length > 0
		const isDropdownActive = isSomethingInDropdownActive && activeIndex === effectiveBaseIndex

		// The separator only earns its place with tabs on its left and the dropdown on its right
		const hasContentBeforeSeparator = shouldShowRouterTabs || visibleOverflowTabs.length > 0

		const dropdown = getDropdownLabelAndCount(
			locateActiveTab(visibleOverflowTabs, dropdownOverflowTabs),
			hiddenOverflowTabsCount,
		)

		// The dropdown lists tabs that are visible in the strip as well as hidden ones, and it reports
		// only the tab. A hidden tab underlines the dropdown button; a visible one takes its own slot.
		const resolveTabIndex = (tab: OverflowTab) => {
			const visibleIndex = visibleOverflowTabs.findIndex(visible => visible.key === tab.key)
			return visibleIndex === -1 ? effectiveBaseIndex : visibleIndex
		}

		const handleOverflowTabClick = (tab: OverflowTab, overflowIndex = resolveTabIndex(tab)) => {
			setActiveIndex(overflowIndex)
			clearFiltersForTabChange()
			tab.onClick?.()
		}

		return (
			<div className='flex items-center gap-1.5 size-full'>
				{/* Visible overflow tabs come first so the indicator can register their refs */}
				{visibleOverflowTabs.map((tab, overflowIndex) => {
					const setOverflowTabRef = (el: HTMLElement | null) => {
						tabRefs.current[overflowIndex] = el
					}
					return (
						<Button
							key={tab.key}
							ref={setOverflowTabRef}
							variant='ghost'
							size='sm'
							className={cn(
								'h-control border-none text-body hover:text-foreground relative z-10 transition-colors',
								tab.isActive && !isSomethingInDropdownActive
									? 'text-header-tab-active-text hover:bg-header-tab-active-bg-hover'
									: 'bg-transparent text-header-tab-inactive-text',
							)}
							aria-current={tab.isActive && !isSomethingInDropdownActive ? 'page' : undefined}
							data-testid={`page-header-overflow-tab-${tab.key}`}
							onClick={() => handleOverflowTabClick(tab, overflowIndex)}>
							<span>{tab.name}</span>
							<TabCountBadge
								count={tab.count}
								active={tab.isActive && !isSomethingInDropdownActive}
								showCount={tab.showCount}
							/>
						</Button>
					)
				})}

				{hasContentBeforeSeparator && shouldShowDropdown && (
					<TabStripSeparator dataTestId='page-header-overflow-separator' />
				)}

				{shouldShowDropdown && (
					<OverflowTabsDropdown
						tabs={dropdownOverflowTabs}
						dropdownLabel={dropdown.label}
						dropdownCount={isDropdownActive ? dropdown.count : null}
						dropdownRef={el => {
							tabRefs.current[effectiveBaseIndex] = el
						}}
						totalTabsCount={totalAllTabs}
						onTabClick={handleOverflowTabClick}
					/>
				)}
			</div>
		)
	}

	return (
		<div className='flex items-center justify-between w-full gap-2'>
			{shouldShowDynamicTabs ? (
				<div className='flex gap-1 min-w-0 flex-1 items-center' data-clears-selection='true'>
					<HeaderBackAndTitleControls backButton={backButton} titleActions={titleActions} />
					{renderDynamicTabs()}
				</div>
			) : (
				<>
					<HeaderBackAndTitleControls
						backButton={backButton}
						titleActions={titleActions}
						backButtonClassName='relative z-10'
					/>
					<div ref={containerRef} className='flex gap-1 min-w-0 flex-1 relative' data-clears-selection='true'>
						{!dynamicTabs && isAnyTabVisible && (
							<SlidingIndicator style={indicatorStyle} dataTestId='page-header-tab-indicator' />
						)}

						<div className='flex relative z-10'>
							{/*
							Router tabs render only for a page that has NOT registered overflow tabs.

							Router tabs come from the navigation structure (nav-data.tsx) and represent child routes;
							overflow tabs are registered explicitly through useRegisterOverflowTabs. A page that
							registers them means to replace its router tabs, not sit alongside them, so showing both
							would duplicate navigation. Registering an empty array is how a page asks for no tabs at
							all (the panel edit view does this).
						*/}
							{shouldShowRouterTabs &&
								(() => {
									const visibleRouterTabs = tabsToDisplay.slice(0, MAX_VISIBLE_TABS)
									return (
										<div className='flex items-center gap-1.5 border-b-0 relative z-10'>
											{visibleRouterTabs.map((item, index) => {
												const pathParts = item.path.split('/')
												const lastPart = pathParts[pathParts.length - 1]
												const tabCount = isAccessRestricted ? 0 : tabCounts[lastPart]
												const { to, params } = navTargetFor(item.path)
												const isActive = activeIndex === index

												const setTabRef = (el: HTMLElement | null) => {
													tabRefs.current[index] = el
												}

												// Without an org id there is no URL to build, only the bare nav path.
												if (!orgId) {
													return null
												}

												return (
													<RouterTab
														key={item.id}
														// @ts-expect-error - Dynamic route path
														to={to}
														params={params}
														search={tabSearch}
														active={isActive}
														onClick={() => handleTabClickWithClear(index)}
														count={tabCount}
														tabRef={setTabRef}
														dataTestId={`page-header-router-tab-${item.id}`}>
														{item.title}
													</RouterTab>
												)
											})}
										</div>
									)
								})()}
							{/* Overflow tabs share the same animated container as the router tabs */}
							<div className='relative z-10'>{renderOverflowTabsSection()}</div>
						</div>
					</div>
				</>
			)}
			{/*
						Right side - page controls, then Action Buttons.

						The slot is for a page with no CTAs that would rather put its own controls here (device
						management does; see `usePageHeaderControls`). It stays ahead of the actions so a page
						that has both keeps its primary CTA rightmost.

						`empty:hidden` is what keeps every other page byte-identical: with nothing portalled in
						the slot is `display:none`, so the row's `gap-2` never allocates space for it. And it
						deliberately carries no `data-clears-selection` — these controls live in the page body
						today, where interacting with them leaves the table selection alone.
					*/}
			{!isAccessRestricted && (
				<div
					ref={setHeaderControlsSlot}
					className='flex shrink-0 items-center gap-2 empty:hidden'
					data-testid='page-header-controls'
				/>
			)}
			{renderActions()}
		</div>
	)
}
